import Vue from "vue";
import store from "@/store";
import router from "@/router";
import { ApiResponse } from "@/api/types";
import { createGaUserId } from "@/helpers/ga-helper";
import { removeZendeskLastAuthDate } from "@/helpers/zendesk-helper";
import { generateSHA256Hash } from "@/helpers/crypto-helper";

const vm = new Vue();
const tokenKey = "occ_authToken";

/*
TODO:
現状、accessToken以外の情報も保存してしまっており、セキュリティ的に問題があるため、
将来的にはpartnerUser取得APIを用意し、accessTokenのみ保存するように変更する
*/
export function saveToken(params: { partnerUser: ApiResponse.PartnerUser }) {
  localStorage.setItem(tokenKey, JSON.stringify(params.partnerUser));
}

export function getToken(): ApiResponse.PartnerUser | undefined {
  const token = localStorage.getItem(tokenKey);
  if (!token) {
    return undefined;
  }
  try {
    return JSON.parse(token);
  } catch {
    // パースに失敗した場合は不正な値なので、ローカルストレージに保存されたアイテムを削除する
    localStorage.removeItem(tokenKey);
    return undefined;
  }
}

export function hasEventApprovedPolicy() {
  const partnerUser = store.state.domains.user.partnerUser;
  if (!partnerUser) return false;
  if (!partnerUser.partner) return false;
  if (!partnerUser.partner.contracts) return false;

  // アクティブな契約
  const now = new Date();
  const contracts = partnerUser.partner.contracts.filter(
    contract =>
      new Date(contract.start_at) < now && new Date(contract.end_at) >= now
  );

  if (!contracts) return false;
  if (contracts.length === 0) return false;

  // アクティブな契約が持つ、イベント作成可能なポリシー
  const policies = contracts
    .map(contract => contract.policies)
    .flat(2)
    .filter(
      policy => policy.resource_name === "event_approved" && policy.level > 1
    );

  if (!policies) return false;
  return policies.length > 0;
}

export function hasApplicationUrlApprovedPolicy(
  contractId: number | undefined
) {
  const partnerUser = store.state.domains.user.partnerUser;
  if (!partnerUser?.partner.contracts) return false;
  if (contractId === undefined) return false;

  // 選択された契約
  const contracts = partnerUser.partner.contracts.filter(
    contract => contract.id === contractId
  );
  if (contracts.length === 0) return false;

  // 外部URL設定ポリシー
  const policies = contracts
    .map(contract => contract.policies)
    .flat(2)
    .filter(
      policy =>
        policy.resource_name === "application_url_approved" &&
        policy.level === 1
    );
  return policies.length > 0;
}

export function setTokenToStore() {
  const token = getToken();
  store.commit("domains/user/updateUser", {
    partnerUser: token
  });
}
export function updateUserWithToken(partnerUser: ApiResponse.PartnerUser) {
  store.commit("domains/user/updateUser", {
    partnerUser
  });
  saveToken({ partnerUser });
}

export async function getPartnerUserAndContracts() {
  await Promise.all([
    store.dispatch("domains/user/getPartnerUser"),
    store.dispatch("domains/user/getContracts")
  ]);
}

export function hasToken() {
  return Boolean(localStorage.getItem(tokenKey));
}

export function removeToken() {
  localStorage.removeItem(tokenKey);
}

export function getUrlQueries() {
  const queryStr = location.search.slice(1); // 文頭?を除外
  const queries: { [key: string]: string } = {};
  if (!queryStr) {
    return queries;
  }
  queryStr.split("&").forEach(queryStr => {
    var queryArr = queryStr.split("=");
    queries[queryArr[0]] = queryArr[1];
  });

  return queries;
}

export function setGAParams() {
  const partnerUser = store.state.domains.user.partnerUser;
  setGAUserParams(partnerUser);
}

/**
 * If `partnerUser` is undefined, set user id to '[GA4] User-ID', and
 * company id & partner id to '[GA4] User Properties', and custom
 * dimension.
 * If `partnerUser` is not undefined, set `null` for all properties.
 */
export function setGAUserParams(
  partnerUser: ApiResponse.PartnerUser | undefined
): void {
  function setParams(
    partnerUserId: number | null,
    companyId: number | null,
    partnerId: number | null,
    customDimension: string | null
  ): void {
    vm.$gtag.set({
      user_id: partnerUserId,
      dimension1: customDimension // カスタムディメンション "uid"
    });
    vm.$gtag.set({
      user_properties: {
        uid: partnerUserId, // NOTE: `user_id` cannot be used here.
        company_id: companyId,
        partner_id: partnerId
      }
    });
  }

  // Initialize user parameters.
  // NOTE: In Google analytics data, the vales should be `null` if they are not exists.
  let partnerUserId = null;
  let companyId = null;
  let partnerId = null;
  let customDimension = null;

  if (!partnerUser) {
    setParams(partnerUserId, companyId, partnerId, customDimension);
    return;
  }

  // Extract user informations from the `partnerUser` object.
  if (partnerUser.id !== undefined) {
    partnerUserId = partnerUser.id;
  }
  if (partnerUser.partner.company.id !== undefined) {
    companyId = partnerUser.partner.company.id;
  }
  if (partnerUser.partner.id !== undefined) {
    partnerId = partnerUser.partner.id;
  }

  // NOTE: all the 3 user parameters must be number if `partnerUser` exists.
  if (partnerUserId == null || companyId == null || partnerId == null) {
    return;
  }
  customDimension = createGaUserId(companyId, partnerId, partnerUserId);

  setParams(partnerUserId, companyId, partnerId, customDimension);
}

export async function authorizationSignInToken(oc_token: string) {
  await store
    .dispatch("domains/user/signInToken", {
      partner_user: {
        oc_token
      }
    })
    .then(() => {
      removeZendeskLastAuthDate();
      router.push({ name: "Home" }).catch(err => err);
    })
    .catch(async () => {
      router.push({ name: "SignIn" }).catch(err => err);
    });
}

export const isLoggedInByOnecareer = (partnerId: number | undefined) => {
  if (!partnerId) return false;

  // パートナーIDを直接記載すると特別なIDが漏洩してしまうリスクがあるためハッシュにした
  const hash =
    "7a61b53701befdae0eeeffaecc73f14e20b537bb0f8b91ad7c2936dc63562b25";
  return generateSHA256Hash(String(partnerId)) === hash;
};

export const getCompanyId = (): number | undefined => {
  return store.state.domains.user.partnerUser?.partner.company.id;
};
